import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaSearch, FaTimes, FaChevronDown, FaChevronUp } from 'react-icons/fa';

const FAQ = ({ isOpen, onClose }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedItems, setExpandedItems] = useState({});
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [showMoreCategories, setShowMoreCategories] = useState(false);

  const faqData = {
    categories: [
      {
        id: 'getting-started',
        title: 'Getting Started with Infuber',
        icon: 'I',
        color: 'bg-orange-500'
      },
      {
        id: 'influencer',
        title: 'For Influencers',
        icon: 'I',
        color: 'bg-blue-500'
      },
      {
        id: 'brands',
        title: 'For Brands',
        icon: 'B',
        color: 'bg-green-500'
      },
      {
        id: 'affiliate',
        title: 'Affiliate Program',
        icon: 'A',
        color: 'bg-purple-500'
      },
      {
        id: 'payments',
        title: 'Payments & Earnings',
        icon: 'P',
        color: 'bg-red-500'
      },
      {
        id: 'technical',
        title: 'Technical Support',
        icon: 'T',
        color: 'bg-indigo-500'
      },
      {
        id: 'account',
        title: 'Account Management',
        icon: 'A',
        color: 'bg-pink-500'
      },
      {
        id: 'campaigns',
        title: 'Campaign Management',
        icon: 'C',
        color: 'bg-yellow-500'
      }
    ],
    faqs: [
      // Getting Started
      {
        id: 'what-is-infuber',
        category: 'getting-started',
        question: 'What is Infuber and how does it work?',
        answer: 'Infuber is a comprehensive influencer marketing platform that connects brands with content creators. We provide tools for campaign management, analytics, affiliate marketing, and payment processing to streamline the entire influencer marketing process.'
      },
      {
        id: 'how-to-signup',
        category: 'getting-started',
        question: 'How do I create an account?',
        answer: 'You can create an account by clicking the "Sign Up" button on our homepage. Choose between "Influencer" or "Brand" account type, fill in your details, and verify your email address. The process takes just a few minutes.'
      },
      {
        id: 'account-types',
        category: 'getting-started',
        question: 'What are the different account types?',
        answer: 'We offer two main account types: Influencer accounts for content creators who want to monetize their social media presence, and Brand accounts for companies looking to collaborate with influencers for marketing campaigns.'
      },

      // For Influencers
      {
        id: 'how-to-earn',
        category: 'influencer',
        question: 'How can I earn money as an influencer?',
        answer: 'You can earn through multiple channels: 1) Advertising campaigns with fixed and variable payouts based on engagement, 2) Affiliate marketing with commission-based earnings, 3) Building your own brand and selling products, and 4) Video production services.'
      },
      {
        id: 'social-media-connection',
        category: 'influencer',
        question: 'How do I connect my social media accounts?',
        answer: 'Go to your dashboard and click "Connect Social Media". We support Instagram, YouTube, Facebook, and other major platforms. You\'ll need to authorize the connection and grant necessary permissions for analytics tracking.'
      },
      {
        id: 'minimum-requirements',
        category: 'influencer',
        question: 'What are the minimum requirements to join as an influencer?',
        answer: 'While we welcome influencers of all sizes, most campaigns require a minimum of 1,000 followers on your primary platform. However, we also have micro-influencer programs for those with smaller but highly engaged audiences.'
      },
      {
        id: 'content-guidelines',
        category: 'influencer',
        question: 'What are the content guidelines?',
        answer: 'All content must comply with platform terms of service and our community guidelines. Content should be authentic, engaging, and align with the brand\'s values. We provide detailed briefs for each campaign to ensure content meets expectations.'
      },

      // For Brands
      {
        id: 'how-to-find-influencers',
        category: 'brands',
        question: 'How do I find the right influencers for my brand?',
        answer: 'Use our advanced search filters to find influencers by niche, follower count, engagement rate, location, and demographics. You can also browse our curated lists or let our AI recommend the best matches based on your campaign goals.'
      },
      {
        id: 'campaign-creation',
        category: 'brands',
        question: 'How do I create a campaign?',
        answer: 'Navigate to "Create Campaign" in your dashboard, fill in campaign details including budget, timeline, deliverables, and target audience. You can then invite specific influencers or open it for applications from our community.'
      },
      {
        id: 'pricing-structure',
        category: 'brands',
        question: 'What are your pricing structures?',
        answer: 'We offer flexible pricing models: fixed project fees, performance-based pricing, and hybrid models. Our platform fee is typically 10-15% of the total campaign value, with no hidden costs or setup fees.'
      },
      {
        id: 'campaign-analytics',
        category: 'brands',
        question: 'What analytics do you provide?',
        answer: 'We provide comprehensive analytics including reach, engagement rates, click-through rates, conversion tracking, and ROI metrics. You\'ll get real-time updates and detailed reports for each campaign.'
      },

      // Affiliate Program
      {
        id: 'affiliate-signup',
        category: 'affiliate',
        question: 'How do I join the affiliate program?',
        answer: 'Apply through your influencer dashboard by clicking "Join Affiliate Program". We\'ll review your application and social media presence. Once approved, you\'ll receive your unique affiliate links and can start earning commissions.'
      },
      {
        id: 'affiliate-commission',
        category: 'affiliate',
        question: 'What commission rates do you offer?',
        answer: 'Commission rates vary by product category and your influencer tier, typically ranging from 5% to 30%. Premium influencers and those with high engagement rates may qualify for higher commission tiers.'
      },
      {
        id: 'affiliate-tracking',
        category: 'affiliate',
        question: 'How do you track affiliate sales?',
        answer: 'We use advanced tracking technology including unique affiliate links, cookies, and conversion pixels to accurately track sales and commissions. All transactions are monitored in real-time through your affiliate dashboard.'
      },
      {
        id: 'affiliate-payments',
        category: 'affiliate',
        question: 'When and how do I receive affiliate payments?',
        answer: 'Affiliate payments are processed monthly on the 15th of each month for the previous month\'s earnings. Payments are made via PayPal, bank transfer, or other preferred methods. Minimum payout threshold is $50.'
      },

      // Payments & Earnings
      {
        id: 'payment-methods',
        category: 'payments',
        question: 'What payment methods do you accept?',
        answer: 'We accept all major credit cards, PayPal, bank transfers, and digital wallets. For international transactions, we also support various local payment methods depending on your region.'
      },
      {
        id: 'payment-security',
        category: 'payments',
        question: 'How secure are payments?',
        answer: 'All payments are processed through industry-standard encryption and PCI-compliant payment processors. We never store your full payment information and use secure tokenization for recurring payments.'
      },
      {
        id: 'payout-schedule',
        category: 'payments',
        question: 'When do I get paid?',
        answer: 'Influencer payments are processed within 7-14 business days after campaign completion and content approval. Affiliate commissions are paid monthly. You can track all payments in your earnings dashboard.'
      },
      {
        id: 'tax-documentation',
        category: 'payments',
        question: 'Do you provide tax documentation?',
        answer: 'Yes, we provide detailed earnings reports and tax documentation for all payments. For US users, we issue 1099 forms for earnings over $600 annually. International users receive appropriate documentation for their jurisdiction.'
      },

      // Technical Support
      {
        id: 'browser-compatibility',
        category: 'technical',
        question: 'Which browsers are supported?',
        answer: 'We support all modern browsers including Chrome, Firefox, Safari, and Edge. For the best experience, we recommend using the latest version of Chrome or Firefox with JavaScript enabled.'
      },
      {
        id: 'mobile-app',
        category: 'technical',
        question: 'Do you have a mobile app?',
        answer: 'Currently, our platform is fully responsive and works great on mobile browsers. We\'re developing native mobile apps for iOS and Android, which will be available in the coming months.'
      },
      {
        id: 'api-access',
        category: 'technical',
        question: 'Do you provide API access?',
        answer: 'Yes, we offer API access for enterprise clients and developers. Our REST API allows you to integrate Infuber functionality into your own applications. Contact our enterprise team for more information.'
      },
      {
        id: 'data-export',
        category: 'technical',
        question: 'Can I export my data?',
        answer: 'Yes, you can export your campaign data, analytics, and earnings reports in various formats including CSV, PDF, and Excel. This feature is available in your account settings under "Data Export".'
      },

      // Account Management
      {
        id: 'profile-verification',
        category: 'account',
        question: 'How do I verify my profile?',
        answer: 'Profile verification helps build trust and unlock premium features. Upload a government-issued ID, verify your phone number, and connect your social media accounts. Our team reviews verification requests within 24-48 hours.'
      },
      {
        id: 'password-reset',
        category: 'account',
        question: 'How do I reset my password?',
        answer: 'Click "Forgot Password" on the login page and enter your email address. You\'ll receive a secure link to reset your password. If you don\'t receive the email, check your spam folder or contact support.'
      },
      {
        id: 'account-deletion',
        category: 'account',
        question: 'How do I delete my account?',
        answer: 'Go to Account Settings > Privacy & Security > Delete Account. This action is irreversible and will remove all your data, campaigns, and earnings history. Make sure to withdraw any pending payments first.'
      },
      {
        id: 'privacy-settings',
        category: 'account',
        question: 'How do I manage my privacy settings?',
        answer: 'Navigate to Account Settings > Privacy & Security to control who can see your profile, contact you, and view your activity. You can also manage data sharing preferences and notification settings.'
      },

      // Campaign Management
      {
        id: 'campaign-approval',
        category: 'campaigns',
        question: 'How long does campaign approval take?',
        answer: 'Most campaigns are reviewed and approved within 24-48 hours. Complex campaigns or those requiring additional verification may take up to 5 business days. You\'ll receive email notifications throughout the process.'
      },
      {
        id: 'content-submission',
        category: 'campaigns',
        question: 'How do I submit content for campaigns?',
        answer: 'Upload your content through the campaign dashboard, including images, videos, and captions. Make sure to follow the brand guidelines and include required hashtags or mentions. Content is reviewed before going live.'
      },
      {
        id: 'campaign-modifications',
        category: 'campaigns',
        question: 'Can I modify a campaign after it\'s created?',
        answer: 'Yes, you can modify campaign details before it\'s approved and influencers are invited. Once influencers start working on the campaign, changes may require their approval. Contact support for assistance with major modifications.'
      },
      {
        id: 'campaign-disputes',
        category: 'campaigns',
        question: 'How do I handle campaign disputes?',
        answer: 'If you have issues with a campaign or influencer, contact our support team immediately. We have a dispute resolution process and can mediate between parties. Most issues are resolved within 48-72 hours.'
      }
    ]
  };

  const filteredFAQs = useMemo(() => {
    let filtered = faqData.faqs;

    // Filter by category
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(faq => faq.category === selectedCategory);
    }

    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(faq => 
        faq.question.toLowerCase().includes(query) || 
        faq.answer.toLowerCase().includes(query)
      );
    }

    return filtered;
  }, [searchQuery, selectedCategory]);

  const visibleCategories = showMoreCategories ? faqData.categories : faqData.categories.slice(0, 3);

  const toggleExpanded = (faqId) => {
    setExpandedItems(prev => ({
      ...prev,
      [faqId]: !prev[faqId]
    }));
  };

  const clearSearch = () => {
    setSearchQuery('');
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            className="bg-white rounded-2xl shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-hidden"
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-orange-500 to-orange-600 text-white p-6">
              <div className="flex justify-between items-start">
                <div>
                  <h2 className="text-3xl font-bold mb-2">Hello there!</h2>
                  <p className="text-orange-100">Ask me a question or two!</p>
                </div>
                <button
                  onClick={onClose}
                  className="text-white hover:text-orange-200 transition-colors duration-200"
                >
                  <FaTimes className="w-6 h-6" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-200px)]">
              {/* Search Bar */}
              <div className="relative mb-6">
                <input
                  type="text"
                  placeholder="Hey there, we are here to help!"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full px-4 py-3 pl-10 pr-10 bg-gray-100 rounded-lg border-0 focus:ring-2 focus:ring-orange-500 focus:bg-white transition-all duration-200"
                />
                <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                {searchQuery && (
                  <button
                    onClick={clearSearch}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    <FaTimes className="w-4 h-4" />
                  </button>
                )}
              </div>

              {/* Need Help Button */}
              <div className="mb-8">
                <button className="w-full bg-gray-100 hover:bg-gray-200 rounded-lg p-4 flex items-center space-x-3 transition-colors duration-200">
                  <div className="w-8 h-8 bg-orange-500 rounded-full flex items-center justify-center text-white font-bold text-sm">
                    IF
                  </div>
                  <span className="text-gray-700 font-medium">Need help? We are here for you!</span>
                </button>
              </div>

              {/* Categories */}
              <div className="mb-8">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-bold text-gray-800">FAQs</h3>
                  <FaSearch className="text-gray-400" />
                </div>
                
                <div className="space-y-3">
                  {visibleCategories.map((category) => (
                    <button
                      key={category.id}
                      onClick={() => setSelectedCategory(category.id)}
                      className={`w-full flex items-center space-x-3 p-3 rounded-lg transition-colors duration-200 ${
                        selectedCategory === category.id 
                          ? 'bg-orange-100 border-2 border-orange-500' 
                          : 'bg-gray-50 hover:bg-gray-100'
                      }`}
                    >
                      <div className={`w-8 h-8 ${category.color} rounded-full flex items-center justify-center text-white font-bold text-sm`}>
                        {category.icon}
                      </div>
                      <span className="text-gray-700 font-medium">{category.title}</span>
                    </button>
                  ))}
                  
                  {faqData.categories.length > 3 && (
                    <button
                      onClick={() => setShowMoreCategories(!showMoreCategories)}
                      className="text-orange-500 hover:text-orange-600 font-medium text-sm"
                    >
                      {showMoreCategories ? 'Show less categories' : 'Show more categories'}
                    </button>
                  )}
                </div>
              </div>

              {/* FAQ Items */}
              <div className="space-y-4">
                {filteredFAQs.length > 0 ? (
                  filteredFAQs.map((faq) => (
                    <motion.div
                      key={faq.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="border border-gray-200 rounded-lg overflow-hidden"
                    >
                      <button
                        onClick={() => toggleExpanded(faq.id)}
                        className="w-full p-4 text-left flex items-center justify-between hover:bg-gray-50 transition-colors duration-200"
                      >
                        <span className="font-medium text-gray-800 pr-4">{faq.question}</span>
                        {expandedItems[faq.id] ? (
                          <FaChevronUp className="text-orange-500 flex-shrink-0" />
                        ) : (
                          <FaChevronDown className="text-orange-500 flex-shrink-0" />
                        )}
                      </button>
                      
                      <AnimatePresence>
                        {expandedItems[faq.id] && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.3 }}
                            className="overflow-hidden"
                          >
                            <div className="px-4 pb-4 text-gray-600 leading-relaxed">
                              {faq.answer}
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </motion.div>
                  ))
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <p>No FAQs found matching your search.</p>
                    <p className="text-sm mt-2">Try adjusting your search terms or browse different categories.</p>
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default FAQ;
